import React, { Dispatch, useState } from 'react'

import DeleteIcon from '@mui/icons-material/Delete'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoIcon from '@mui/icons-material/Info'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography
} from '@mui/material'
import Select, { SelectChangeEvent } from '@mui/material/Select'

import { AppState } from '../../../config/types'
import {
  ProductAcabadosPeraltes,
  ProductAcabadosPeraltesAnchos,
  ProductComplement,
  ProductComplementUniones,
  ProductSelected
} from '../../../config/types/contentTypes'
import { DispatchAction } from '../../../config/types/contextTypes'
import { getProductComplement } from '../../../context/actions/productComplementActions'
import {
  DEFAULT_SELECT_ITEM,
  updateProductSelected
} from '../../../context/actions/productSelectedActions'
import { useStateValue } from '../../../context/store'
import { themeCharofil } from '../../../styles/styles'
import Loading from '../../loading'
import { useStyles } from '../useStyles'

const TramosRectos2Form: React.FC = () => {
  const classes = useStyles()
  const { state, dispatch }: { state: AppState; dispatch: Dispatch<DispatchAction> } =
    useStateValue()

  const productSelected: ProductSelected | undefined = state?.productSelected
  const productComplement: ProductComplement | undefined = state?.productComplement

  const [verMas, setVerMas] = useState<ProductComplementUniones | undefined>(undefined)

  const handlePeralteChange = async (event: SelectChangeEvent) => {
    if (!productSelected) return

    if (parseInt(event.target.value) === 0) {
      const newProductSelected: ProductSelected = {
        ...productSelected,
        ancho: DEFAULT_SELECT_ITEM,
        union: DEFAULT_SELECT_ITEM,
        soporte: DEFAULT_SELECT_ITEM,
        anchoSelect: 0,
        pasoSelect: 0,
        peralte: DEFAULT_SELECT_ITEM,
        uniones: [],
        soportes: [],
        derivaciones: new Map<number, number>(),
      }
      await updateProductSelected(dispatch, newProductSelected)
    } else {
      productSelected.acabado?.peraltes.forEach(async (itemPeralte) => {
        if (itemPeralte.id === parseInt(event.target.value)) {
          const newProductSelected: ProductSelected = {
            ...productSelected,
            ancho: DEFAULT_SELECT_ITEM,
            union: DEFAULT_SELECT_ITEM,
            soporte: DEFAULT_SELECT_ITEM,
            anchoSelect: 0,
            pasoSelect: 0,
            peralte: itemPeralte,
            uniones: [],
            soportes: [],
            derivaciones: new Map<number, number>(),
          }
          await updateProductSelected(dispatch, newProductSelected)
        }
      })
    }
  }

  const handlePeralteAnchoChange = async (event: SelectChangeEvent) => {
    if (!productSelected || !productSelected?.peralte) return

    const newProductSelected: ProductSelected = {
      ...productSelected,
      ancho: DEFAULT_SELECT_ITEM,
      union: DEFAULT_SELECT_ITEM,
      soporte: DEFAULT_SELECT_ITEM,
      uniones: [],
      soportes: [],
      derivaciones: new Map<number, number>(),
    }
    if (parseInt(event.target.value) !== 0) {
      productSelected.peralte?.anchos?.forEach((itemAncho) => {
        if (itemAncho.ancho === parseInt(event.target.value)) {
          newProductSelected.anchoSelect = itemAncho.ancho
          newProductSelected.ancho = itemAncho
          newProductSelected.pasoSelect = 0
          return
        }
      })
    } else {
      newProductSelected.anchoSelect = parseInt(event.target.value)
    }

    await updateProductSelected(dispatch, newProductSelected)
  }

  const handlePasoChange = async (event: SelectChangeEvent) => {
    if (!productSelected || !productSelected?.peralte || !productSelected?.ancho) return

    const newProductSelected: ProductSelected = {
      ...productSelected,
      pasoSelect: parseInt(event.target.value),
    }

    await updateProductSelected(dispatch, newProductSelected)

    // llamar productComplement
    if (newProductSelected.ancho) {
      await getProductComplement(
        dispatch,
        productSelected.id,
        productSelected.peralte.id,
        newProductSelected.ancho.id
      )
    }
  }

  const handleOnChangeMetros = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!productSelected) return

    const metros: number = parseInt(event.target.value.replace(/[^0-9]/g, ''))
    const newProductSelected: ProductSelected = { ...productSelected, metros: metros }
    await updateProductSelected(dispatch, newProductSelected)
  }

  const handleUnionesChange = async (event: SelectChangeEvent) => {
    if (!productSelected || !productComplement?.uniones || !event.target?.value) return

    if (parseInt(event.target.value) === 0)
      await updateProductSelected(dispatch, { ...productSelected, union: DEFAULT_SELECT_ITEM })
    else if (productComplement) {
      productComplement.uniones.forEach(async (union) => {
        if (union.id === parseInt(event.target.value)) {
          await updateProductSelected(dispatch, { ...productSelected, union: union })
          return
        }
      })
    }
  }

  const handleAddUnionOnClick = async () => {
    if (!productSelected || !productSelected?.union) return

    if (productSelected.union.id !== 0) {
      const uniones = productSelected.uniones ? productSelected.uniones : []
      uniones.push(productSelected.union)

      const data = {
        union: DEFAULT_SELECT_ITEM,
        uniones: uniones,
      }
      await updateProductSelected(dispatch, { ...productSelected, ...data })
    }
  }

  const handleEliminarUnionOnClick = async (index: number) => {
    if (!productSelected || !productSelected?.uniones) return

    let uniones = productSelected.uniones
    uniones.splice(index, 1)
    await updateProductSelected(dispatch, { ...productSelected, uniones })
  }

  const getPeraltes = (peraltes: ProductAcabadosPeraltes[]) => {
    if (!productSelected) return []

    const displayPeraltes: ProductAcabadosPeraltes[] = []
    peraltes.forEach((peralte) => {
      if (
        peralte.perfil?.id === productSelected.perfilSelect &&
        peralte.longitud === productSelected.longitudSelect &&
        peralte.tipoPeldaño === productSelected.peldañoSelect
        // && !displayPeraltes.some((item) => item.name === peralte.name)
      ) {
        displayPeraltes.push(peralte)
      }
    })

    return displayPeraltes
  }

  const getAnchos = (anchos: ProductAcabadosPeraltesAnchos[]): number[] => {
    if (!productSelected || !productSelected.peralte || !anchos) return []

    const displayAnchos: number[] = []
    anchos.forEach((ancho) => {
      if (
        ancho.peralte_id === productSelected.peralte?.id &&
        ancho.ancho &&
        !displayAnchos.includes(ancho.ancho)
      )
        displayAnchos.push(ancho.ancho)
    })
    return displayAnchos
  }

  const getPasos = (anchos: ProductAcabadosPeraltesAnchos[]) => {
    if (!productSelected || !anchos || productSelected?.anchoSelect === 0) return []

    const displayPasos: ProductAcabadosPeraltesAnchos[] = []
    anchos.forEach((anchoItem) => {
      if (anchoItem.ancho === productSelected.anchoSelect) displayPasos.push(anchoItem)
    })

    return displayPasos
  }

  const handleCloseVerMas = () => {
    setVerMas(undefined)
  }

  const handleVerMasOnClick = async (index: number) => {
    if (!productSelected || !productSelected?.uniones) return
    setVerMas(productSelected.uniones[index])
  }

  return productSelected ? (
    <Grid container direction="column" className={classes.cardContainer}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl className={classes.selectInput}>
            <ThemeProvider theme={themeCharofil}>
              <InputLabel id="peralte-select-label">Seleccione un Peralte:</InputLabel>
              <Select
                fullWidth
                labelId="peralte-select-label"
                id="peralte-select"
                key="peralte-select"
                label="Seleccione un Peralte:"
                value={productSelected.peralte ? `${productSelected.peralte.id}` : '0'}
                onChange={handlePeralteChange}
              >
                <MenuItem key={`peralte-empty`} value={0}>
                  -
                </MenuItem>
                {productSelected.acabado
                  ? productSelected.acabado.peraltes
                    ? getPeraltes(productSelected.acabado.peraltes).map((currentPeralte) => (
                        <MenuItem key={`peralte-${currentPeralte.id}`} value={currentPeralte.id}>
                          {currentPeralte.name}
                        </MenuItem>
                      ))
                    : ''
                  : ''}
              </Select>
            </ThemeProvider>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl className={classes.selectInput}>
            <ThemeProvider theme={themeCharofil}>
              <InputLabel id="peralte-ancho-select-label">Seleccione un Ancho:</InputLabel>
              <Select
                fullWidth
                labelId="peralte-ancho-select-label"
                id="peralte-ancho-select"
                key="peralte-ancho-select"
                label="Seleccione un Ancho:"
                value={productSelected.anchoSelect ? `${productSelected.anchoSelect}` : '0'}
                onChange={handlePeralteAnchoChange}
              >
                <MenuItem key={`peralte-ancho-empty`} value={0}>
                  -
                </MenuItem>
                {productSelected.peralte &&
                  productSelected.peralte.anchos &&
                  getAnchos(productSelected.peralte.anchos).map((ancho) => (
                    <MenuItem key={`peralte-ancho-${ancho}`} value={ancho}>
                      {ancho}
                    </MenuItem>
                  ))}
              </Select>
            </ThemeProvider>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl className={classes.selectInput}>
            <ThemeProvider theme={themeCharofil}>
              <InputLabel id="peralte-paso-select-label">Seleccione un Paso:</InputLabel>
              <Select
                fullWidth
                labelId="peralte-paso-select-label"
                id="peralte-paso-select"
                key="peralte-paso-select"
                label="Seleccione un Paso:"
                value={productSelected.pasoSelect ? `${productSelected.pasoSelect}` : '0'}
                onChange={handlePasoChange}
              >
                <MenuItem key={`peralte-paso-empty`} value={0}>
                  -
                </MenuItem>
                {productSelected.peralte &&
                  productSelected.peralte.anchos &&
                  getPasos(productSelected.peralte.anchos).map((ancho) => (
                    <MenuItem key={`peralte-paso-${ancho.pasos?.paso}`} value={ancho.pasos?.paso}>
                      {ancho.pasos?.paso}
                    </MenuItem>
                  ))}
              </Select>
            </ThemeProvider>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <ThemeProvider theme={themeCharofil}>
            <TextField
              variant="outlined"
              required
              fullWidth
              id="metros"
              label="Metros Requeridos"
              inputProps={{ style: { textAlign: 'center' } }}
              name="metros"
              value={`${productSelected.metros ? productSelected.metros : ''}`}
              onChange={handleOnChangeMetros}
            />
          </ThemeProvider>
        </Grid>
        <Grid item xs={12}>
          <Accordion>
            <Grid item xs={12} className={classes.expandGrid}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-label="Expand"
                aria-controls="añadir-uniones-content"
                id="añadir-uniones-header"
              >
                <Typography color="textPrimary" variant="subtitle2">
                  Añadir Uniones
                  <br />
                  <Typography variant="body2" component="span" color="textSecondary">
                    {productSelected.uniones
                      ? productSelected.uniones.length > 0
                        ? productSelected.uniones.length === 1
                          ? `${productSelected.uniones.length} seleccionada`
                          : `${productSelected.uniones.length} seleccionadas`
                        : `Aún no ha seleccionado uniones`
                      : `Aún no ha seleccionado uniones`}
                  </Typography>
                </Typography>
              </AccordionSummary>
            </Grid>
            <Grid item xs={12}>
              <AccordionDetails>
                <Grid container item spacing={3}>
                  <Grid item xs={12}>
                    <FormControl className={classes.selectInput}>
                      <ThemeProvider theme={themeCharofil}>
                        <InputLabel id="uniones-select-label">Uniones Disponibles:</InputLabel>
                        <Select
                          fullWidth
                          labelId="uniones-select-label"
                          id="uniones-select"
                          key="uniones-select"
                          label="Uniones Disponibles:"
                          value={productSelected.union ? `${productSelected.union.id}` : '0'}
                          onChange={handleUnionesChange}
                        >
                          <MenuItem key={`uniones-empty`} value={0}>
                            -
                          </MenuItem>
                          {productComplement &&
                          (productSelected.peralte?.id !== 0 || productSelected.ancho?.id !== 0)
                            ? productComplement.uniones
                              ? productComplement.uniones.map((union) => (
                                  <MenuItem key={`uniones-${union.id}`} value={union.id}>
                                    {union.nombre}
                                  </MenuItem>
                                ))
                              : ''
                            : ''}
                        </Select>
                      </ThemeProvider>
                    </FormControl>
                    <Grid className={classes.expandGrid2} />
                  </Grid>
                  {productSelected.union ? (
                    productSelected.union.id !== 0 ? (
                      <Grid item xs={12}>
                        <FormControl className={classes.selectInput}>
                          <Grid item xs={12}>
                            <Grid className={classes.expandGrid2} />
                            <Grid item xs={12}>
                              <Button
                                type="submit"
                                variant="contained"
                                onClick={handleAddUnionOnClick}
                                style={{ backgroundColor: '#BE0A15' }}
                              >
                                Añadir
                              </Button>
                            </Grid>
                            <Grid className={classes.expandGrid2} />
                          </Grid>
                        </FormControl>
                      </Grid>
                    ) : (
                      <Grid className={classes.expandGrid2} />
                    )
                  ) : (
                    <Grid className={classes.expandGrid2} />
                  )}
                </Grid>
              </AccordionDetails>
            </Grid>
            <Grid container item spacing={3}>
              <Grid container spacing={0} direction="column" className={classes.complGrid} />
              {productSelected.uniones
                ? productSelected.uniones.map((cartUnion, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={`card-union-selected-${index}`}>
                      <Card>
                        <CardActionArea>
                          <CardMedia
                            component="img"
                            alt={cartUnion.nombre}
                            image={cartUnion.img}
                            title={cartUnion.nombre}
                            height="220"
                            sx={{ padding: '1em 1em 0 0', objectFit: 'contain' }}
                          />
                          <CardContent>
                            <Grid style={{ height: 60 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                {cartUnion.nombre}
                              </Typography>
                            </Grid>
                            <Grid>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                component="p"
                                // align="justify"
                              >
                                {cartUnion.descripcion?.substring(0, 50)}...
                              </Typography>
                            </Grid>
                          </CardContent>
                        </CardActionArea>
                        <CardActions>
                          <Grid alignItems={'flex-start'} style={{ width: '50%' }}>
                            <Tooltip title="Eliminar" placement="top">
                              <IconButton
                                aria-label="Eliminar"
                                onClick={() => handleEliminarUnionOnClick(index)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                          <Grid alignItems={'flex-end'} style={{ width: '50%' }}>
                            <Tooltip title="Ver más" placement="top">
                              <IconButton
                                aria-label="Ver más"
                                onClick={() => handleVerMasOnClick(index)}
                              >
                                <InfoIcon fontSize="small" style={{ color: '#BE0A15' }} />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))
                : ''}
            </Grid>
          </Accordion>
        </Grid>
      </Grid>
      {verMas ? (
        <Grid>
          <Dialog
            fullWidth
            maxWidth="sm"
            open={verMas !== undefined}
            onClose={handleCloseVerMas}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">{verMas?.nombre}</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                <Grid>{verMas?.descripcion}</Grid>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <ThemeProvider theme={themeCharofil}>
                <Button onClick={handleCloseVerMas} color="primary" autoFocus>
                  Cerrar
                </Button>
              </ThemeProvider>
            </DialogActions>
          </Dialog>
        </Grid>
      ) : null}
    </Grid>
  ) : (
    <Loading />
  )
}
export default TramosRectos2Form
